import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout, Wrench, Coins, ArrowRight, Sparkles } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import GTFLogo, { GTF_LOGO } from "@/components/GTFLogo";

const LOGO = GTF_LOGO;
const HERO = "https://images.unsplash.com/photo-1608335715837-1994a535d5c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxsdXNoJTIwZ3JlZW4lMjBmYXJtJTIwc3Vuc2V0fGVufDB8fHx8MTc4NDY4NDQ3Nnww&ixlib=rb-4.1.0&q=85";

const cards = [
  { role: "farmer", label: "Farmers", icon: Sprout, tid: HOME.farmerCard, gradient: "from-emerald-600/90 to-emerald-900/95", accent: "text-emerald-100",
    img: "https://images.unsplash.com/photo-1628492058844-589eb5dc6a35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYXJtZXIlMjBwb3J0cmFpdCUyMGZpZWxkfGVufDB8fHx8MTc4NDY4NDQ3Nnww&ixlib=rb-4.1.0&q=85",
    desc: "Sell crops, buy equipment, connect with buyers across India." },
  { role: "day_worker", label: "Day Workers", icon: Wrench, tid: HOME.workerCard, gradient: "from-blue-600/90 to-blue-900/95", accent: "text-blue-100",
    img: "https://images.pexels.com/photos/7807035/pexels-photo-7807035.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    desc: "Find local jobs — plumbing, driving, painting, and more." },
  { role: "investor", label: "Investors", icon: Coins, tid: HOME.investorCard, gradient: "from-amber-500/90 to-amber-800/95", accent: "text-amber-50",
    img: "https://images.pexels.com/photos/8463151/pexels-photo-8463151.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    desc: "Discover startups, fund projects, and grow your portfolio." },
];

export default function Landing() {
  const nav = useNavigate();
  const { t } = useI18n();
  const localizedCards = cards.map((c) => ({
    ...c,
    label: c.role === "farmer" ? t("role.farmer") : c.role === "day_worker" ? t("role.worker") : t("role.investor"),
    desc: c.role === "farmer" ? t("role.farmer.desc") : c.role === "day_worker" ? t("role.worker.desc") : t("role.investor.desc"),
  }));
  return (
    <div className="min-h-screen relative">
      {/* Hero */}
      <div className="relative min-h-[100vh] overflow-hidden">
        <img src={HERO} alt="Farm at sunset" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-emerald-950/50 to-black/80" />

        <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 lg:px-24 py-6">
          <GTFLogo size={56} showText textVariant="light" />
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="dark" />
            <Link data-testid={HOME.loginLink} to="/login" className="text-white/90 hover:text-white text-sm font-medium border border-white/25 rounded-full px-5 py-2 hover:bg-white/10 transition-all">
              Sign In
            </Link>
          </div>
        </nav>

        <div className="relative z-10 px-6 md:px-12 lg:px-24 pt-8 md:pt-20 pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-emerald-100 text-xs tracking-[0.2em] uppercase backdrop-blur-xl">
              <Sparkles className="h-3.5 w-3.5" /> {t("landing.chip")}
            </div>
            <h1 className="hero-title mt-6 text-white text-5xl md:text-7xl lg:text-8xl font-bold max-w-4xl">
              {t("hero.title")}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-emerald-300 to-emerald-500">{t("hero.brand")}</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid={HOME.heroCta} onClick={() => nav("/role")}
                className="group inline-flex items-center gap-2 bg-white text-emerald-800 hover:bg-emerald-50 px-8 py-4 rounded-full font-semibold shadow-2xl transition-all">
                {t("hero.get_started")}
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => document.getElementById("roles")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full font-medium">
                {t("hero.explore")}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Roles */}
      <section id="roles" className="px-6 md:px-12 lg:px-24 py-24 bg-[#FAFAF7]">
        <div className="max-w-6xl">
          <div className="text-xs font-semibold tracking-[0.24em] uppercase text-emerald-700">Choose your path</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold text-gray-900 tracking-tight max-w-3xl">{t("roles.title")} <span className="text-emerald-600">{t("roles.title_you")}</span> {t("roles.title_grow")}</h2>
          <p className="mt-4 text-gray-600 text-lg max-w-2xl">{t("roles.subtitle")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {localizedCards.map((c, idx) => (
            <motion.div key={c.role} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: idx * 0.12, duration: 0.6 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              data-testid={c.tid} onClick={() => nav("/role", { state: { preselect: c.role } })}
              className="group cursor-pointer relative rounded-3xl overflow-hidden h-[440px] premium-shadow">
              <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className={`absolute inset-0 bg-gradient-to-t ${c.gradient}`} />
              <div className="relative z-10 h-full flex flex-col justify-between p-8">
                <div className="flex items-center justify-between">
                  <div className={`h-14 w-14 rounded-2xl bg-white/15 backdrop-blur-xl border border-white/25 flex items-center justify-center ${c.accent}`}>
                    <c.icon className="h-7 w-7" />
                  </div>
                  <div className="text-xs text-white/70 tracking-[0.2em] uppercase">Role 0{idx + 1}</div>
                </div>
                <div>
                  <div className="text-white text-3xl md:text-4xl font-bold tracking-tight">{c.label}</div>
                  <p className={`mt-3 ${c.accent} text-sm md:text-base leading-relaxed max-w-xs`}>{c.desc}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-white font-medium">
                    {t("hero.get_started")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Founder / Footer */}
      <footer className="bg-emerald-950 text-emerald-100/80 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="GTF" className="h-14 w-14 rounded-2xl bg-white p-1" />
              <div>
                <div className="text-white text-lg font-bold tracking-tight">Grow Together Farmily</div>
                <div className="text-xs tracking-widest uppercase text-emerald-300/70">GTF · Made in India</div>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed max-w-sm">{t("footer.tagline")}</p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-emerald-300">{t("footer.founder")}</div>
            <div className="mt-3 text-white text-2xl font-bold tracking-tight">Nagaraja G</div>
            <div className="mt-1 text-sm">Bellary, Karnataka · India</div>
            <a href="mailto:growtogetherfarmily899@gmail.com" className="mt-3 inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 text-sm font-medium">
              growtogetherfarmily899@gmail.com
            </a>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-emerald-300">{t("footer.explore")}</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><span className="text-white/90">Farmers Marketplace</span></li>
              <li><span className="text-white/90">Day Workers Directory</span></li>
              <li><span className="text-white/90">Investor Opportunities</span></li>
              <li><span className="text-white/90">Support & Help</span></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/10 text-xs flex flex-wrap items-center justify-between gap-4">
          <div>© 2026 Grow Together Farmily. {t("footer.rights")}</div>
          <div className="text-emerald-300/60">{t("footer.builtwith")}</div>
        </div>
      </footer>
    </div>
  );
}
